import { memo, useEffect, useState } from 'react';
import { useApp } from '../../../../hook/app.hook';
import { Question, Section } from '../../../../response/test.response';

const JeeSummaryMComponent = () => {
  const { testRes } = useApp();

  const [summary, setSummary] = useState({
    notVisitedCount: 0,
    notAnsweredCount: 0,
    answeredCount: 0,
    markedForReviewCount: 0,
    answeredAndMarkedCount: 0,
  });

  const getQuestionStatus = (question: Question) => {
    const visited =
      question.timeTaken > 0 ||
      (question.attempted && question.saved) ||
      question.markedForRevisit ||
      question.active;

    if (!visited) return 'not-visited';
    if (question.attempted && question.saved && question.markedForRevisit)
      return 'answered-and-marked';
    if (question.attempted && question.saved) return 'answered';
    if (question.markedForRevisit) return 'marked-for-review';
    return 'not-answered';
  };

  useEffect(() => {
    let _notVisitedCount = 0;
    let _notAnsweredCount = 0;
    let _answeredCount = 0;
    let _markedForReviewCount = 0;
    let _answeredAndMarkedCount = 0;

    testRes.sectionList.forEach((section: Section) => {
      section.questionList.forEach((question: Question) => {
        const status = getQuestionStatus(question);
        if (status === 'not-visited') {
          _notVisitedCount++;
        } else if (status === 'not-answered') {
          _notAnsweredCount++;
        } else if (status === 'answered') {
          _answeredCount++;
        } else if (status === 'marked-for-review') {
          _markedForReviewCount++;
        } else if (status === 'answered-and-marked') {
          _answeredAndMarkedCount++;
        }
      });
    });

    setSummary({
      notVisitedCount: _notVisitedCount,
      notAnsweredCount: _notAnsweredCount,
      answeredCount: _answeredCount,
      markedForReviewCount: _markedForReviewCount,
      answeredAndMarkedCount: _answeredAndMarkedCount,
    });
  }, [testRes]);

  const StatusItem = ({
    count,
    label,
    bgColor,
    textColor = 'white',
    shape = 'trapezoid',
    hasMarker = false,
  }: {
    count: number;
    label: string;
    bgColor: string;
    textColor?: string;
    shape?: 'trapezoid' | 'circle' | 'rounded';
    hasMarker?: boolean;
  }) => {
    const getShapeStyle = (): React.CSSProperties => {
      const base: React.CSSProperties = {
        width: '28px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: '600',
        fontSize: '11px',
        color: textColor,
        backgroundColor: bgColor,
        position: 'relative',
      };

      if (shape === 'circle') {
        return { ...base, borderRadius: '50%', width: '26px', height: '26px' };
      }
      if (shape === 'rounded') {
        return { ...base, borderRadius: '4px' };
      }
      // trapezoid
      return {
        ...base,
        clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)',
      };
    };

    return (
      <div className="d-flex flex-column align-items-center">
        <div style={getShapeStyle()}>
          {count}
          {hasMarker && (
            <div
              style={{
                position: 'absolute',
                bottom: '1px',
                right: '1px',
                width: '8px',
                height: '8px',
                backgroundColor: '#28a745',
                borderRadius: '1px',
              }}
            />
          )}
        </div>
        <span
          className="mt-1 text-center"
          style={{
            fontSize: '9px',
            lineHeight: '1.1',
            maxWidth: '50px',
            color: '#FFFFFF',
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  return (
    <div
      className="d-flex justify-content-around align-items-start py-2 px-1 rounded"
      style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
    >
      <StatusItem
        count={summary.notVisitedCount}
        label="Not Visited"
        bgColor="#e0e0e0"
        textColor="#666"
        shape="rounded"
      />
      <StatusItem
        count={summary.notAnsweredCount}
        label="Not Answered"
        bgColor="#dc3545"
        shape="trapezoid"
      />
      <StatusItem
        count={summary.answeredCount}
        label="Answered"
        bgColor="#28a745"
        shape="trapezoid"
      />
      <StatusItem
        count={summary.markedForReviewCount}
        label="Review"
        bgColor="#6f42c1"
        shape="circle"
      />
      <StatusItem
        count={summary.answeredAndMarkedCount}
        label="Ans+Review"
        bgColor="#6f42c1"
        shape="circle"
        hasMarker
      />
    </div>
  );
};

export default memo(JeeSummaryMComponent);
